import React from 'react';
import {Card, Tag, Tooltip} from 'antd';
import {useNavigate} from 'react-router-dom';
import {ROUTES} from '../../utils/ROUTES';
import {capitalizeFirstLetter} from '../../utils/TextAlterations';
import {formatCurrency} from '../../utils/currency';
import {WorkOutlineOutlined, AttachMoney} from '@mui/icons-material';
import {ScrollShadow} from '@heroui/react';
import {InfoCircleFilled} from '@ant-design/icons';
import {MapPin, Building} from 'lucide-react';

interface RecommendedJobsProps {
	jobs: any[];
	title?: string;
}

const RecommendedJobs: React.FC<RecommendedJobsProps> = ({jobs, title = 'Similar jobs'}) => {
	const navigate = useNavigate();

	if (!jobs || jobs.length === 0) {
		return null;
	}

	return (
		<div className="h-full">
			{title && (
				<div className="mb-6">
					<h3 className="text-lg font-medium text-neutral-800 mb-2 flex items-center gap-2">
						{title}
						<Tooltip title="These jobs are similar to the one you are viewing. You can apply to any of them.">
							<InfoCircleFilled className="text-sm text-neutral-400 hover:text-neutral-600 transition-colors" />
						</Tooltip>
					</h3>
				</div>
			)}
			<ScrollShadow
				className="max-h-[calc(100dvh)] h-full overflow-y-auto"
				style={{
					scrollbarWidth: 'thin',
					scrollbarColor: '#e5e7eb transparent',
				}}>
				<div className="space-y-4">
					{jobs.map((job) => (
						<div
							key={job.id}
							className="bg-white rounded-lg border border-neutral-200/60 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer group"
							onClick={() => navigate(ROUTES.JOB_PAGE.replace(':jobId', job.id))}>
							<div className="p-4">
								{/* Company and logo */}
								<div className="flex items-start gap-3 mb-3">
									<div className="flex-shrink-0 w-10 h-10 rounded-lg border border-neutral-200/60 flex items-center justify-center overflow-hidden p-1">
										{job?.created_by_image ? (
											<img src={job.created_by_image} alt="company logo" className="w-full h-full object-cover rounded-lg" />
										) : (
											<Building className="w-5 h-5 text-neutral-400" />
										)}
									</div>
									<div className="flex-1 min-w-0">
										<p className="text-sm font-medium text-neutral-800 truncate">{job?.company_name && capitalizeFirstLetter(job.company_name)}</p>
									</div>
								</div>

								{/* Job title */}
								<h4 className="text-base font-semibold text-neutral-900 mb-3 line-clamp-2 leading-snug group-hover:text-neutral-700 transition-colors">
									{capitalizeFirstLetter(job.title)}
								</h4>

								{/* Job type tags */}
								<div className="flex flex-wrap gap-2 mb-4">
									{job.engagement_type && (
										<span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-neutral-100 text-neutral-700 border border-neutral-200">
											{job.engagement_type}
										</span>
									)}
									{job.job_type && (
										<span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-neutral-50 text-neutral-600 border border-neutral-200/60">
											{job.job_type}
										</span>
									)}
								</div>

								{/* Salary and location */}
								<div className="space-y-2">
									{job?.payment_rate_from && job?.show_salary_on_job && (
										<div className="flex items-center gap-2 text-sm text-neutral-600">
											<div className="flex-shrink-0 w-5 h-5 bg-emerald-50/70 rounded flex items-center justify-center">
												<AttachMoney style={{fontSize: '12px', color: '#059669'}} />
											</div>
											<span className="truncate font-medium text-neutral-800">
												{formatCurrency(job?.payment_rate_from, job?.payment_rate_to, job?.payment_rate_type, job?.payment_rate_currency)}
											</span>
										</div>
									)}

									{job?.role_location_type && (
										<div className="flex items-center gap-2 text-sm text-neutral-600">
											<div className="flex-shrink-0 w-5 h-5 bg-neutral-50 rounded flex items-center justify-center">
												<MapPin className="w-3 h-3 text-neutral-500" />
											</div>
											<span className="truncate capitalize">
												{job.role_location_type} {job.role_location_type.toLowerCase() === 'remote' && '(Philippines)'}
											</span>
										</div>
									)}
								</div>
							</div>
						</div>
					))}
				</div>
			</ScrollShadow>
		</div>
	);
};

export default RecommendedJobs;
